import { Fragment } from "react";
import { Box, Divider, MenuItem, Typography, useTheme } from "@mui/material";
import styled from "@emotion/styled";
import { BOTTOM_SHEET_Z_INDEX, CloseButton, Modal } from "@rentablo/components";
import {
  ResponsiveDropdownGroup,
  ResponsiveDropdownItem,
  itemLinkProps,
} from "./common";

const SheetHeading = styled("div", {
  shouldForwardProp: (prop) => prop !== "withDivider",
})<{ withDivider?: boolean }>(({ theme, withDivider }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: theme.spacing(0, -4),
  padding: theme.spacing(0, 4, 4),
  ...(withDivider && {
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
  }),
}));

const SheetItemsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  margin: theme.spacing(0, -4),
  minHeight: 0,
  overflowY: "auto",
}));

// Grouped mode: inset cards (no full-bleed negative margin) separated by a gap.
const SheetGroupsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  paddingTop: theme.spacing(4),
  minHeight: 0,
  overflowY: "auto",
}));

const SheetGroupHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: theme.spacing(3, 4),
  color: theme.palette.text.secondary,
}));

const GroupHeaderDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.border.secondary,
}));

const SheetMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "indent",
})<{ indent?: number }>(({ theme, indent = 0 }) => ({
  padding: theme.spacing(2, 4, 2, 4 + indent),
  // MUI's 48px phone minimum would undo the tighter padding.
  minHeight: 0,
  borderRadius: 8,
}));

const SheetDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(0, 4),
}));

const SheetItem = ({
  item,
  onItemClick,
  indent = 0,
}: {
  item: ResponsiveDropdownItem;
  onItemClick: (item: ResponsiveDropdownItem) => void;
  indent?: number;
}) => (
  <SheetMenuItem
    data-testid={item.testId}
    selected={item.selected}
    onClick={() => onItemClick(item)}
    {...itemLinkProps(item)}
    indent={indent}
  >
    <Box
      display="flex"
      alignItems="center"
      gap={3}
      color={item.color ?? "textPrimary"}
    >
      {item.icon}
      <Typography variant="body3" color={item.color ?? "textPrimary"}>
        {item.label}
      </Typography>
    </Box>
  </SheetMenuItem>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  items?: ResponsiveDropdownItem[];
  groups?: ResponsiveDropdownGroup[];
  onItemClick: (item: ResponsiveDropdownItem) => void;
}

export const ResponsiveDropdownSheet = ({
  isOpen,
  onClose,
  title,
  items,
  groups,
  onItemClick,
}: Props) => {
  const theme = useTheme();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        title ? (
          <SheetHeading withDivider={Boolean(groups)}>
            <Typography variant="h6">{title}</Typography>
            <CloseButton onClose={onClose} data-testid="modal-close-button" />
          </SheetHeading>
        ) : undefined
      }
      width="100%"
      height="fit-content"
      maxHeight="100%"
      position="bottom"
      zIndex={BOTTOM_SHEET_Z_INDEX}
      disablePortal={false}
      contentPadding={theme.spacing(3, 4, 4)}
    >
      {groups ? (
        <SheetGroupsContainer>
          {groups.map((group, groupIndex) => (
            <Box key={group.title ?? groupIndex}>
              <SheetGroupHeader>
                {group.icon}
                <Typography
                  variant="body3"
                  fontWeight={600}
                  color="textPrimary"
                >
                  {group.title}
                </Typography>
              </SheetGroupHeader>
              <GroupHeaderDivider />
              {group.items.map((item) => (
                <SheetItem
                  key={item.label}
                  item={item}
                  onItemClick={onItemClick}
                  indent={8}
                />
              ))}
            </Box>
          ))}
        </SheetGroupsContainer>
      ) : (
        <SheetItemsContainer>
          {items?.map((item, index) => (
            <Fragment key={item.label}>
              {item.startsGroup && index > 0 && <SheetDivider />}
              <SheetItem item={item} onItemClick={onItemClick} />
            </Fragment>
          ))}
        </SheetItemsContainer>
      )}
    </Modal>
  );
};
